/**
 * useContentEditableState - State Management für ContentEditable mit Asset-Badges.
 */
import { useCallback, useState, useRef } from 'react';
import { insertAssetBadge, parseContentEditable, createSafeBadgeElement, type AssetBadgeData } from '@/lib/utils/contentEditable';

export interface UseContentEditableStateProps {
  onChange?: (plainText: string, assetIds: string[]) => void;
}

export function useContentEditableState({ onChange }: UseContentEditableStateProps = {}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [selectedBadgeId, setSelectedBadgeId] = useState<string | null>(null);

  /**
   * Parst Content und triggert onChange.
   */
  const triggerChange = useCallback(() => {
    if (!editorRef.current || !onChange) return;

    const { plainText, assetIds } = parseContentEditable(editorRef.current);
    onChange(plainText, assetIds);
  }, [onChange]);

  /**
   * Fügt Asset-Badge an Cursor-Position ein.
   */
  const insertBadge = useCallback((badgeData: AssetBadgeData) => {
    if (!editorRef.current) return;

    insertAssetBadge(editorRef.current, badgeData);
    triggerChange();
  }, [triggerChange]);

  /**
   * Badge-Click-Handler (öffnet Dropdown).
   */
  const handleBadgeClick = useCallback((assetId: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();

    setSelectedBadgeId(assetId);
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    });
    setDropdownOpen(true);
  }, []);

  /**
   * Ersetzt Badge mit neuem Asset.
   */
  const replaceBadge = useCallback((oldAssetId: string, newBadgeData: AssetBadgeData) => {
    if (!editorRef.current) return;

    const badge = editorRef.current.querySelector(`[data-asset-id="${oldAssetId}"]`);
    if (!badge) return;

    // Create new badge element (safe DOM API, no innerHTML)
    const newBadge = createSafeBadgeElement(newBadgeData);

    badge.parentNode?.replaceChild(newBadge, badge);
    triggerChange();
  }, [triggerChange]);

  return {
    editorRef,
    insertBadge,
    handleBadgeClick,
    replaceBadge,
    triggerChange,
    dropdownOpen,
    setDropdownOpen,
    dropdownPosition,
    selectedBadgeId,
  };
}
